import { observable, computed, action, makeObservable } from 'mobx'
import { FILTER_SETTINGS } from '../constants'
import RangeFilter from './RangeFilter'

class Filter {
  @observable type = FILTER_SETTINGS.ALL
  @observable status = ''
  @observable tags = []
  @observable searchQuery = ''

  // Range filters as instances
  population
  density
  avgAge
  genderRatio
  income

  _defaultType
  _defaultStatus = ''
  _defaultSearchQuery = ''

  constructor (defaults = {}) {
    makeObservable(this)
    this._defaultType = defaults.type || 'All'

    // Initialize range filters with defaults
    this.population = new RangeFilter(defaults.population || [0, 100])
    this.density = new RangeFilter(defaults.density || [0, 100])
    this.avgAge = new RangeFilter(defaults.avgAge || [0, 100])
    this.genderRatio = new RangeFilter(defaults.genderRatio || [0, 1])
    this.income = new RangeFilter(defaults.income || defaults.hhSize || defaults.hh_size || [0, 10])

    this.type = this._defaultType
  }

  // Convenient getters for backward compatibility
  @computed get populationMin () { return this.population.min }
  @computed get populationMax () { return this.population.max }
  @computed get densityMin () { return this.density.min }
  @computed get densityMax () { return this.density.max }
  @computed get avgAgeMin () { return this.avgAge.min }
  @computed get avgAgeMax () { return this.avgAge.max }
  @computed get genderRatioMin () { return this.genderRatio.min }
  @computed get genderRatioMax () { return this.genderRatio.max }
  @computed get incomeMin () { return this.income.min }
  @computed get incomeMax () { return this.income.max }
  @computed get hhSize () { return this.income }
  @computed get hhSizeMin () { return this.income.min }
  @computed get hhSizeMax () { return this.income.max }

  @computed get typeValue () {
    return (this.type && this.type.value) || this.type
  }

  @computed get statusValue () {
    // "All" is `{ value: '', label: 'All' }`, so unwrap objects explicitly
    // rather than falling back to the (falsy-valued) object itself.
    return this.status && typeof this.status === 'object' ? this.status.value : this.status
  }

  @computed get tagIds () {
    return this.tags.map(tag => tag.id)
  }

  @computed get isTagsAtDefault () {
    return this.tags.length === 0
  }

  @computed get isStatusAtDefault () {
    return this.statusValue === this._defaultStatus
  }

  @computed get isTypeAtDefault () {
    return this.typeValue === this._defaultType
  }

  @computed get isAtDefaults () {
    return (
      this.isTypeAtDefault &&
      this.isStatusAtDefault &&
      this.isTagsAtDefault &&
      this.population.isAtDefault &&
      this.density.isAtDefault &&
      this.avgAge.isAtDefault &&
      this.genderRatio.isAtDefault &&
      (this.income.disabled || this.income.isAtDefault)
    )
  }

  @action resetType () {
    this.type = this._defaultType
  }

  @action resetStatus () {
    this.status = this._defaultStatus
  }

  @action resetTags () {
    this.tags = []
  }

  @action resetFilters () {
    this.resetType()
    this.resetStatus()
    this.resetTags()
    this.searchQuery = this._defaultSearchQuery
    this.population.reset()
    this.density.reset()
    this.avgAge.reset()
    this.genderRatio.reset()
    this.income.reset()
  }
}

export default Filter
